import { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { shipmentsApi, type ShipmentStatus } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { StatusPill, type PillTone } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDateTime, formatDate } from "@/shared/datetime";

const NEXT: Record<ShipmentStatus, ShipmentStatus[]> = {
  Planned:    ["Loaded", "Exception"],
  Loaded:     ["Dispatched", "Exception"],
  Dispatched: ["InTransit", "Exception"],
  InTransit:  ["Arrived", "Exception"],
  Arrived:    ["Delivered", "Exception"],
  Delivered:  ["Closed", "Exception"],
  Closed:     [],
  Exception:  ["InTransit", "Delivered", "Closed"],
};
const STATUS_TONE: Record<string, PillTone> = {
  Planned: "neutral", Loaded: "info", Dispatched: "warning", InTransit: "warning",
  Arrived: "info", Delivered: "success", Closed: "neutral", Exception: "critical",
};

/**
 * UC-M2-TR-02 — Shipment detail: header, line table, milestone timeline, and POD
 * capture dialog. Status advances via /milestones; the server enforces allowed transitions.
 */
export function ShipmentDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canMilestone = useAuth(s => s.hasPermission("transport.shipment.milestone"));
  const canPod = useAuth(s => s.hasPermission("transport.shipment.pod"));

  const [milestoneStatus, setMilestoneStatus] = useState<ShipmentStatus | "">("");
  const [milestoneNotes, setMilestoneNotes] = useState("");
  const [milestoneOpen, setMilestoneOpen] = useState(false);
  const [podOpen, setPodOpen] = useState(false);
  const [receiverName, setReceiverName] = useState("");
  const [podNotes, setPodNotes] = useState("");
  const [signatureUrl, setSignatureUrl] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");

  const query = useQuery({
    queryKey: ["shipment", id],
    queryFn: () => shipmentsApi.get(id),
    enabled: !!id,
  });

  const milestoneMutation = useMutation({
    mutationFn: () => shipmentsApi.addMilestone(id, milestoneStatus as ShipmentStatus,
      milestoneNotes || undefined, query.data!.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipment", id] });
      qc.invalidateQueries({ queryKey: ["shipments"] });
      setMilestoneOpen(false); setMilestoneStatus(""); setMilestoneNotes("");
    },
  });

  const podMutation = useMutation({
    mutationFn: () => shipmentsApi.capturePod(id, {
      receiverName,
      signatureUrl: signatureUrl || undefined,
      photoUrl: photoUrl || undefined,
      notes: podNotes || undefined,
    }, query.data!.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipment", id] });
      setPodOpen(false); setReceiverName(""); setPodNotes(""); setSignatureUrl(""); setPhotoUrl("");
    },
  });

  if (query.isLoading || !query.data) return <DetailSkeleton />;
  const s = query.data;
  const allowedNext = NEXT[s.status];
  const canCapturePod = (s.status === "Arrived" || s.status === "Delivered") && canPod;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>Shipment {s.transferOrderNo}</Typography>
          <StatusPill label={s.status} tone={STATUS_TONE[s.status] ?? "neutral"} />
          {s.podStatus && <Chip label={`POD ${s.podStatus}`} size="small" color="success" />}
        </Stack>
        <Stack direction="row" spacing={1}>
          {canMilestone && allowedNext.length > 0 && (
            <Button variant="contained" startIcon={<TimelineIcon />}
              onClick={() => setMilestoneOpen(true)}>
              Advance status
            </Button>
          )}
          {canCapturePod && (
            <Button variant="contained" color="success" startIcon={<AssignmentTurnedInIcon />}
              onClick={() => setPodOpen(true)}>
              {s.pod ? "Update POD" : "Capture POD"}
            </Button>
          )}
        </Stack>
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack><Typography variant="caption" color="text.secondary">Carrier</Typography>
            <Typography>{s.carrierName ?? "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Vehicle</Typography>
            <Typography>{s.vehicleRegNo ?? s.vehicleNo ?? "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Driver</Typography>
            <Typography>{s.driverName ?? "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">E-way bill</Typography>
            <Typography>{s.eWayBillNo ?? "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Requested delivery</Typography>
            <Typography>{s.requestedDeliveryDate ? formatDate(s.requestedDeliveryDate) : "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Actual delivery</Typography>
            <Typography>{s.actualDeliveryDate ? formatDateTime(s.actualDeliveryDate) : "—"}</Typography></Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Timeline</Typography>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>When</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {s.milestones.map(m => (
              <TableRow key={m.id}>
                <TableCell>{formatDateTime(m.occurredAt)}</TableCell>
                <TableCell><StatusPill label={m.status} tone={STATUS_TONE[m.status] ?? "neutral"} /></TableCell>
                <TableCell>{m.notes ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {s.pod && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Proof of delivery</Typography>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Stack><Typography variant="caption" color="text.secondary">Receiver</Typography>
              <Typography>{s.pod.receiverName}</Typography></Stack>
            <Stack><Typography variant="caption" color="text.secondary">Captured</Typography>
              <Typography>{formatDateTime(s.pod.capturedAt)}</Typography></Stack>
            {s.pod.signatureUrl && (
              <Stack><Typography variant="caption" color="text.secondary">Signature</Typography>
                <Typography component="a" href={s.pod.signatureUrl} target="_blank">View</Typography></Stack>
            )}
            {s.pod.photoUrl && (
              <Stack><Typography variant="caption" color="text.secondary">Photo</Typography>
                <Typography component="a" href={s.pod.photoUrl} target="_blank">View</Typography></Stack>
            )}
          </Stack>
          {s.pod.notes && <Typography sx={{ mt: 2, whiteSpace: "pre-wrap" }}>{s.pod.notes}</Typography>}
        </Paper>
      )}

      <Button onClick={() => navigate("/transport/shipments")}>Back to list</Button>

      <Dialog open={milestoneOpen} onClose={() => setMilestoneOpen(false)} maxWidth="xs" fullWidth>
        <ClosableDialogTitle onClose={() => setMilestoneOpen(false)}>Advance shipment status</ClosableDialogTitle>
        <DialogContent>
          <TextField select label="New status *" fullWidth sx={{ mt: 1 }}
            value={milestoneStatus} onChange={(e) => setMilestoneStatus(e.target.value as ShipmentStatus)}>
            {allowedNext.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </TextField>
          <TextField label="Notes" fullWidth multiline minRows={2} sx={{ mt: 2 }}
            value={milestoneNotes} onChange={(e) => setMilestoneNotes(e.target.value)} />
          {milestoneMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(milestoneMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMilestoneOpen(false)}>Cancel</Button>
          <Button variant="contained"
            disabled={!milestoneStatus || milestoneMutation.isPending}
            onClick={() => milestoneMutation.mutate()}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={podOpen} onClose={() => setPodOpen(false)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setPodOpen(false)}>{s.pod ? "Update POD" : "Capture POD"}</ClosableDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Receiver name *" value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)} />
            <TextField label="Signature URL" value={signatureUrl} placeholder="https://…"
              onChange={(e) => setSignatureUrl(e.target.value)} />
            <TextField label="Photo URL" value={photoUrl} placeholder="https://…"
              onChange={(e) => setPhotoUrl(e.target.value)} />
            <TextField label="Notes" multiline minRows={2}
              value={podNotes} onChange={(e) => setPodNotes(e.target.value)} />
            {podMutation.isError && (
              <Alert severity="error">{errorMessage(podMutation.error)}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPodOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={!receiverName || podMutation.isPending}
            onClick={() => podMutation.mutate()}>
            Save POD
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
